/* eslint-disable react/display-name */
import { Modal } from '@mui/material';
import { useRouteParams } from 'app/shared/hooks/useRouteParams';
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { ModalHeader, ModalVariant } from '../ModalHeader';
import { CustomTypography } from '../Typography/Typography';
import { Button } from '../Button/Button';
import { translations } from 'app/locales/i18n';
import { useTranslation } from 'react-i18next';
import { useStyles } from './ReuseModalItemInfo.styles';
import { IReuseOrderModel, ReuseAction, ReuseResult } from 'types';
import { useBatchHistoryList } from 'app/shared/hooks/useBatchHistoryList';

export interface IReuseModalItemInfoRef {
  open: (
    type: ReuseResult,
    message: string,
    reuseOrderModel: IReuseOrderModel,
  ) => void;
  close: () => void;
}

interface IReuseModalItemInfoProps {
  refreshHandler: () => void;
}
export const ReuseModalItemInfo = forwardRef(
  (
    { refreshHandler }: IReuseModalItemInfoProps,
    ref: ForwardedRef<IReuseModalItemInfoRef | null>,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const { classes } = useStyles();
    const { t } = useTranslation();
    const [info, setInfo] = useState<
      | {
          title: string;
          message: string;
          type: ReuseResult;
          reuseOrderModel: IReuseOrderModel;
        }
      | undefined
    >(undefined);
    const { organizationId }: { organizationId: string } = useRouteParams();
    const { createReuseConcreteActionPost } = useBatchHistoryList({
      organizationId,
    });

    useImperativeHandle(ref, () => ({
      open: (type, message, reuseOrderModel) => {
        setIsOpen(true);
        setInfo({
          title:
            type === ReuseResult.SUCCESS
              ? t(translations.reuseModelsItem.reuseResult.success)
              : t(translations.reuseModelsItem.reuseResult.warning),
          message,
          type,
          reuseOrderModel,
        });
      },
      close: () => {
        setIsOpen(false);
        setInfo(undefined);
      },
    }));

    const closeHandler = useCallback(() => {
      setIsOpen(false);
    }, []);

    const proceedHandler = useCallback(async () => {
      if (!info?.reuseOrderModel) return;
      try {
        await createReuseConcreteActionPost({
          returnConcreteOrderDataId:
            info?.reuseOrderModel.returnConcreteOrderDataId,
          partitionDateUtc: info?.reuseOrderModel.partitionDateUtc,
          reuseAction: ReuseAction.PROCEED,
        });
      } catch (error) {
        console.log(error);
      }
      setIsOpen(false);

      refreshHandler();
    }, [createReuseConcreteActionPost, info?.reuseOrderModel, refreshHandler]);

    const removeOrderHandler = useCallback(async () => {
      if (!info?.reuseOrderModel) return;
      try {
        await createReuseConcreteActionPost({
          returnConcreteOrderDataId:
            info?.reuseOrderModel.returnConcreteOrderDataId,
          partitionDateUtc: info?.reuseOrderModel.partitionDateUtc,
          reuseAction: ReuseAction.REMOVE,
        });
      } catch (error) {
        console.log(error);
      }
      setIsOpen(false);
      refreshHandler();
    }, [createReuseConcreteActionPost, info?.reuseOrderModel, refreshHandler]);

    return (
      <Modal
        open={isOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            return false;
          }
          setIsOpen(false);
        }}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        className={classes.modal}
      >
        <div className={classes.small}>
          <div className={classes.container}>
            <ModalHeader
              variant={ModalVariant.SMALL}
              header={info?.title ?? ''}
              color={
                info?.type === ReuseResult.SUCCESS ? 'systemGreen' : 'mbsOrange'
              }
              closeHandler={() => setIsOpen(false)}
            />

            <div className={classes.body}>
              <CustomTypography variant="bodyLarge" color="accentLight">
                {info?.message}
              </CustomTypography>
            </div>
            <div className={classes.saveButton} data-testid="button">
              {info?.type === ReuseResult.WARNING && (
                <>
                  <Button variant="primarySave" onClick={proceedHandler}>
                    <CustomTypography variant="buttonText" bold color="white">
                      {t(translations.reuseModelsItem.proceed)}
                    </CustomTypography>
                  </Button>
                  <Button
                    variant="primaryCancel"
                    onClick={removeOrderHandler}
                    className={classes.removeOrderButton}
                  >
                    <CustomTypography variant="buttonText" bold color="mbsBlue">
                      {t(translations.reuseModelsItem.removeOrder)}
                    </CustomTypography>
                  </Button>
                  <Button variant="primaryCancel" onClick={closeHandler}>
                    <CustomTypography variant="buttonText" bold color="mbsBlue">
                      {t(translations.reuseModelsItem.cancel)}
                    </CustomTypography>
                  </Button>
                </>
              )}
              {info?.type === ReuseResult.SUCCESS && (
                <Button variant="primarySave" onClick={closeHandler}>
                  <CustomTypography variant="buttonText" bold color="white">
                    {t(translations.errors.okay)}
                  </CustomTypography>
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal>
    );
  },
);
